/**
 * Developer Token Scope Options
 *
 * Lists the comedians and venues a developer can scope a token to. Token access
 * is read-only public data (a comedian's / venue's shows), so ANY published
 * comedian and ANY live venue page is selectable — not just ones the developer
 * administers. Names for the token LIST are resolved separately by id.
 */

import { SupabaseClient } from "@supabase/supabase-js";

import { Database } from "../../supabase.types";
import {
  DeveloperTokenScope,
  DeveloperTokenScopeOption,
  DeveloperTokenScopeOptions,
} from "../../models/widget-api.types";
import { getPublishedComedians } from "../../legacy/comedian";
import { getPublishedLiveVenuePages } from "../public/venues";
import { logException } from "@punchup/sentry";

/**
 * Returns every published comedian and live venue page as { id, name } options
 * for the token-creation scope picker, each sorted alphabetically by name. Both
 * are public read (no session required) — a developer can mint a read-only
 * token for any of them. Comedians also carry their `slug`, which the docs
 * playgrounds surface alongside the id.
 */
export async function getDeveloperTokenScopeOptions(): Promise<DeveloperTokenScopeOptions> {
  const [comedians, venues] = await Promise.all([
    getPublishedComedians(),
    getPublishedLiveVenuePages(),
  ]);

  const byName = (a: DeveloperTokenScopeOption, b: DeveloperTokenScopeOption) =>
    a.name.localeCompare(b.name);

  return {
    comedians: comedians
      .map((c) => ({ id: c.id, name: c.display_name, slug: c.slug }))
      .sort(byName),
    venues: venues.map((v) => ({ id: v.id, name: v.name })).sort(byName),
  };
}

/**
 * Whether a requested scope is one a developer may actually mint a token for.
 * The picker only ever offers published comedians / live venues, but that is a
 * client-side list — a crafted request can name any id, so the API must recheck
 * against the same source before persisting the scope.
 *
 * @param scope - The comedian or venue scope requested by the caller
 * @returns True if the scope is currently selectable
 */
export async function isDeveloperTokenScopeSelectable(
  scope: DeveloperTokenScope
): Promise<boolean> {
  const { comedians, venues } = await getDeveloperTokenScopeOptions();

  return scope.type === "comedian"
    ? comedians.some((c) => c.id === scope.comedianId)
    : venues.some((v) => v.id === scope.venuePageId);
}

/**
 * Batch-resolves display names for a set of comedian and venue ids, for the
 * token list's Scope cell. Reuses the same publicly-readable lookups as the
 * scope picker; returns a `Map<id, name>` per axis (missing ids are simply
 * absent from the map). Skips a query for an empty id list.
 */
export async function resolveScopeNames(
  supabase: SupabaseClient<Database>,
  { comedianIds, venueIds }: { comedianIds: string[]; venueIds: string[] }
): Promise<{ comedians: Map<string, string>; venues: Map<string, string> }> {
  // Names are display-only for the token list, so a resolution failure (already
  // logged by the resolvers) degrades to blank names rather than failing the
  // whole list. The picker path does NOT tolerate — it lets the error surface.
  try {
    const [comedians, venues] = await Promise.all([
      resolveComedians(supabase, comedianIds),
      resolveVenues(supabase, venueIds),
    ]);

    return {
      comedians: new Map(comedians.map((c) => [c.id, c.name])),
      venues: new Map(venues.map((v) => [v.id, v.name])),
    };
  } catch {
    return { comedians: new Map(), venues: new Map() };
  }
}

async function resolveComedians(
  supabase: SupabaseClient<Database>,
  ids: string[]
): Promise<DeveloperTokenScopeOption[]> {
  if (ids.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("comedians")
    .select("id, display_name")
    .in("id", ids);

  if (error) {
    logException(error, {
      tags: { what: "Error resolving comedian scope options", supabase: true },
    });
    throw new Error(
      `Failed to resolve comedian scope options: ${error.message}`
    );
  }

  return (data ?? []).map((c) => ({ id: c.id, name: c.display_name }));
}

async function resolveVenues(
  supabase: SupabaseClient<Database>,
  ids: string[]
): Promise<DeveloperTokenScopeOption[]> {
  if (ids.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("venue_pages")
    .select("id, name")
    .in("id", ids);

  if (error) {
    logException(error, {
      tags: { what: "Error resolving venue scope options", supabase: true },
    });
    throw new Error(`Failed to resolve venue scope options: ${error.message}`);
  }

  return (data ?? []).map((v) => ({ id: v.id, name: v.name }));
}
